import React, { useState, useEffect } from 'react';
import { List, message, Button, Alert } from 'antd';
import { ref, query, limitToLast, get } from 'firebase/database';
import { realtimeDb } from '../firebaseConfig';
import { getDoc, doc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db as firestoreDb } from '../firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import lemonLogo from '../assets/logo.png';

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40vh;
`;

/* Плавное появление элементов списка (тот же приём, что в Home) */
const cardVariants = {
  hidden: { opacity: 0, y: 4 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
};

/* -------------------------------------------------------------------------- */
/*                                 Component                                  */
/* -------------------------------------------------------------------------- */

const InvoicesList = () => {
  const { currentUser } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  /* ----------------------------- fetch logic ----------------------------- */
  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        if (!currentUser) {
          message.error('Nie jesteś zalogowany.');
          return;
        }

        // Получаем driverId из Firestore
        const userDocRef = doc(firestoreDb, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
          message.error('Nie można znaleźć danych użytkownika.');
          return;
        }

        const driverId = userDoc.data().driverId;

        // Запрос последних 5 записей из Realtime Database
        const invoicesRef   = ref(realtimeDb, `/drivers/${driverId}/invoices`);
        const invoicesQuery = query(invoicesRef, limitToLast(5));
        const snapshot      = await get(invoicesQuery);

        if (snapshot.exists()) {
          const invoicesData = [];
          snapshot.forEach((childSnapshot) => {
            invoicesData.push(childSnapshot.val());
          });
          setInvoices(invoicesData.reverse());
        } else {
          setInvoices([]);
        }
      } catch (error) {
        console.error('Ошибка при загрузке faktur:', error);
        message.error('Wystąpił błąd przy ładowaniu faktur.');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [currentUser]);

  /* ------------------------------ helpers ------------------------------ */
  const getAlertType = (status) => {
    switch (status) {
      case 'zaakceptowany':       return { type: 'success', color: '#52c41a' };
      case 'odrzucony':           return { type: 'error',   color: '#ff4d4f' };
      case 'w trakcie sprawdzenia': return { type: 'warning', color: '#faad14' };
      default:                    return { type: 'info',    color: '#1890ff' };
    }
  };

  /* ------------------------------ render ------------------------------ */
  return (
    <div>
      {/* Заголовок и кнопка */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, marginBottom: 20 }}>
        <h2>Ostatnie faktury</h2>
        <Button
          type="primary"
          onClick={() => navigate('/faktury')}
          style={{
            color: 'black',
            fontWeight: 700,
            backgroundColor: '#e4b61a',
            borderColor: '#e4b61a',
          }}
        >
          Więcej
        </Button>
      </div>

      {/* Лоадер или список */}
      {loading ? (
        <LoaderWrapper>
          <motion.img
            src={lemonLogo}
            alt="Ładowanie"
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 2, ease: 'linear' }}
            style={{ width: 64, height: 64 }}
          />
        </LoaderWrapper>
      ) : (
        <AnimatePresence>
          {invoices.length > 0 ? (
            <motion.div initial="hidden" animate="show" exit="hidden">
              <List
                itemLayout="vertical"
                dataSource={invoices}
                renderItem={(item, index) => {
                  const { type, color } = getAlertType(item.status);
                  return (
                    <motion.div variants={cardVariants} key={index}>
                      <Alert
                        type={type}
                        showIcon={false}
                        style={{
                          padding: 12,
                          border: `1px solid ${color}`,
                          borderRadius: 8,
                          marginBottom: 12,
                          backgroundColor: `${color}1a`,
                        }}
                        message={
                          <>
                            <List.Item.Meta
                              title={`Faktura: ${item.numerfaktury}`}
                              description={`Status: ${item.status}`}
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <p>Kwota VAT: <strong>{parseFloat(item.vatAmount).toFixed(2)}</strong> zł</p>
                              <p>Kwota brutto: <strong>{parseFloat(item.grossAmount).toFixed(2)}</strong> zł</p>
                            </div>
                          </>
                        }
                      />
                    </motion.div>
                  );
                }}
              />
            </motion.div>
          ) : (
            <Alert
              type="info"
              message="Brak faktur"
              description="Nie znaleziono żadnych faktur dla tego użytkownika."
              showIcon
            />
          )}
        </AnimatePresence>
      )}
    </div>
  );
};

export default InvoicesList;
